import { useState, type FormEvent } from 'react';
import { ApiError, api, apiErrorCode } from '../api';
import type { User } from '../types';

export function MemberPasswordPanel({ currentUser, notify }: {
  currentUser: User;
  notify: (message: string, tone?: 'success' | 'error') => void;
}) {
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [challengeId, setChallengeId] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function requestCode() {
    const challenge = await api.requestAccountOtp(currentUser.email);
    setChallengeId(challenge.challengeId);
    setCode('');
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    if (passwords.next !== passwords.confirm) return setError('The new password confirmation does not match.');
    if (passwords.next === passwords.current) return setError('Choose a new password that differs from your current password.');
    setBusy(true);
    try {
      if (challengeId) {
        // Reuse the existing one-time email sign-in proof; it refreshes the
        // same Member's session and CSRF token before the protected change.
        const verified = await api.verifyAccountOtp(currentUser.email, challengeId, code);
        setChallengeId('');
        setCode('');
        if (verified.id !== currentUser.id) throw new Error('Account verification did not match. Reload before continuing.');
      }
      await api.changePassword(passwords.current, passwords.next);
      setPasswords({ current: '', next: '', confirm: '' });
      notify('Password changed. Other sessions and trusted browsers have been signed out or forgotten.');
    } catch (reason) {
      if (apiErrorCode(reason) === 'member_email_verification_required') {
        try { await requestCode(); }
        catch (requestError) { setError(requestError instanceof ApiError ? requestError.message : 'Unable to send a verification code. Please try again.'); }
      } else {
        setError(reason instanceof Error ? reason.message : 'Unable to change your password.');
      }
    } finally { setBusy(false); }
  }

  async function resend() {
    setBusy(true); setError('');
    try { await requestCode(); }
    catch (reason) { setError(reason instanceof ApiError ? reason.message : 'Unable to send a new code. Please try again.'); }
    finally { setBusy(false); }
  }

  return <><section className="workspace-section" aria-labelledby="member-password-title">
    <div className="workspace-section__heading"><div><h2 id="member-password-title">Change your password</h2><p>Use at least 12 characters. Email verification may be required, even on a trusted browser.</p></div></div>
    <p className="form-help">Your current browser stays signed in. Other sessions are signed out and all browsers must verify their next password sign-in.</p>
    {error && <p className="form-alert" role="alert">{error}</p>}
    <form className="dialog-form" onSubmit={submit}>
      <label>Current password<input type="password" autoComplete="current-password" required disabled={busy || Boolean(challengeId)} value={passwords.current} onChange={event => setPasswords(value => ({ ...value, current: event.target.value }))} /></label>
      <div className="form-section__grid">
        <label>New password<input type="password" autoComplete="new-password" minLength={12} required disabled={busy || Boolean(challengeId)} value={passwords.next} onChange={event => setPasswords(value => ({ ...value, next: event.target.value }))} /></label>
        <label>Confirm new password<input type="password" autoComplete="new-password" minLength={12} required disabled={busy || Boolean(challengeId)} value={passwords.confirm} onChange={event => setPasswords(value => ({ ...value, confirm: event.target.value }))} /></label>
      </div>
      {challengeId && <>
        <p className="form-help" role="status">Enter the six-digit code sent to {currentUser.email} here to continue. The code expires in 10 minutes. Use the code here rather than the email's sign-in link.</p>
        <label>Email verification code<input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required disabled={busy} value={code} onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} /></label>
      </>}
      <div className="form-actions">
        <button className="button button--primary" type="submit" disabled={busy}>{busy ? 'Please wait…' : challengeId ? 'Verify code & change password' : 'Change password'}</button>
        {challengeId && <><button className="button button--secondary" type="button" disabled={busy} onClick={() => void resend()}>Send a new code</button><button className="button button--secondary" type="button" disabled={busy} onClick={() => { setChallengeId(''); setCode(''); setPasswords({ current: '', next: '', confirm: '' }); setError(''); }}>Cancel</button></>}
      </div>
    </form>
  </section><section className="workspace-section" aria-labelledby="member-email-title"><div className="workspace-section__heading"><h2 id="member-email-title">Account email</h2></div><p>{currentUser.email}</p><p className="form-help">Contact your service operator to request an email change. Your current address stays active until the new address is verified. This does not change your Sejoli checkout profile.</p></section></>;
}
